import React, { useState, useCallback, useEffect } from 'react';
import { YearbookElement, Position, cornerRadiiToCss, elementShadowToCss } from '@/types/yearbook';

interface SlotRendererProps {
  element: YearbookElement;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (id: string, updates: Partial<YearbookElement>) => void;
  onDragUpdate: (id: string, updates: Partial<YearbookElement>) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  scale: number;
  elementIndex?: number;
}

const SlotRenderer: React.FC<SlotRendererProps> = ({
  element,
  isSelected,
  onSelect,
  onUpdate,
  onDragUpdate,
  onDragStart,
  onDragEnd,
  scale,
  elementIndex,
}) => {
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Position>({ x: 0, y: 0 });
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const [imgError, setImgError] = useState(false);
  const [fullLoaded, setFullLoaded] = useState(false);

  // Reset states when imageUrl changes
  useEffect(() => {
    setImgError(false);
    setFullLoaded(false);
  }, [element.imageUrl]);

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      if (!element.imageRatio) {
        const img = e.currentTarget;
        setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
      }
    },
    [element.imageRatio]
  );

  const effectiveRatio = element.imageRatio || (naturalSize ? naturalSize.w / naturalSize.h : null);

  // Compute max pan range in percentage (0-100)
  const getPanBounds = useCallback(() => {
    const scale = element.imageScale || 1;
    if (!effectiveRatio && scale <= 1) return { maxPanX: 0, maxPanY: 0, canPanX: false, canPanY: false };
    const slotRatio = element.size.width / element.size.height;
    // With object-fit:cover, one axis fills exactly and the other overflows
    const baseCanPanX = effectiveRatio ? effectiveRatio > slotRatio : false;
    const baseCanPanY = effectiveRatio ? effectiveRatio < slotRatio : false;
    // When zoomed in, both axes overflow so both can be panned
    const canPanX = scale > 1 ? true : baseCanPanX;
    const canPanY = scale > 1 ? true : baseCanPanY;
    return { maxPanX: canPanX ? 100 : 0, maxPanY: canPanY ? 100 : 0, canPanX, canPanY };
  }, [effectiveRatio, element.size, element.imageScale]);

  const startPan = useCallback(
    (clientX: number, clientY: number) => {
      if (!effectiveRatio && !(element.imageScale && element.imageScale > 1)) return;
      onDragStart();
      setIsPanning(true);
      const offset = element.imageOffset || { x: 50, y: 50 };
      setPanStart({
        x: clientX + offset.x * scale,
        y: clientY + offset.y * scale,
      });
    },
    [effectiveRatio, element.imageOffset, onDragStart, scale]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      startPan(e.clientX, e.clientY);
    },
    [startPan]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return;
      e.stopPropagation();
      startPan(e.touches[0].clientX, e.touches[0].clientY);
    },
    [startPan]
  );

  useEffect(() => {
    if (!isPanning) return;

    const bounds = getPanBounds();

    const movePan = (clientX: number, clientY: number) => {
      const rawX = (panStart.x - clientX) / scale;
      const rawY = (panStart.y - clientY) / scale;

      const clampedX = bounds.canPanX ? Math.min(100, Math.max(0, rawX)) : 50;
      const clampedY = bounds.canPanY ? Math.min(100, Math.max(0, rawY)) : 50;

      onDragUpdate(element.id, {
        imageOffset: { x: Math.round(clampedX), y: Math.round(clampedY) },
      });
    };

    const handleMouseMove = (e: MouseEvent) => movePan(e.clientX, e.clientY);
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        movePan(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const endPan = () => {
      setIsPanning(false);
      onDragEnd();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', endPan);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', endPan);
    window.addEventListener('touchcancel', endPan);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', endPan);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', endPan);
      window.removeEventListener('touchcancel', endPan);
    };
  }, [isPanning, panStart, getPanBounds, element.id, onDragUpdate, onDragEnd, scale]);

  const offset = element.imageOffset || { x: 50, y: 50 };
  const displaySrc = (!fullLoaded && element.thumbnail) ? element.thumbnail : element.imageUrl;
  return (
    <div
      data-element-id={element.id}
      className={`absolute overflow-hidden ${isSelected ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
      style={{
        left: element.position.x,
        top: element.position.y,
        width: element.size.width,
        height: element.size.height,
        transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
        zIndex: isSelected ? 50 : (element.zIndex != null ? element.zIndex : (element.zPriority === 'front' ? 100 + (elementIndex ?? 0) : element.zPriority === 'back' ? (elementIndex ?? 0) : 10 + (elementIndex ?? 0))),
        borderRadius: cornerRadiiToCss(element.cornerRadii) || (element.isCircle ? '50%' : element.borderRadius ? `${element.borderRadius}px` : '0'),
        backgroundColor: element.backgroundColor || undefined,
        border: element.borderColor ? `1px solid ${element.borderColor}` : undefined,
        boxShadow: elementShadowToCss(element.shadow),
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      {element.imageUrl && !imgError ? (
        <div
          className="w-full h-full overflow-hidden relative"
          style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Show thumbnail instantly while full image loads in background */}
          {element.thumbnail && !fullLoaded && (
            <img
              src={element.thumbnail}
              alt="Slot preview"
              draggable={false}
              className="w-full h-full pointer-events-none select-none absolute inset-0"
              style={{
                objectFit: 'cover',
                objectPosition: `${offset.x}% ${offset.y}%`,
                transform: element.imageScale && element.imageScale > 1 ? `scale(${element.imageScale})` : undefined,
                transformOrigin: `${offset.x}% ${offset.y}%`,
              }}
            />
          )}
          <img
            src={element.imageUrl}
            alt="Slot image"
            draggable={false}
            onLoad={(e) => {
              setFullLoaded(true);
              handleImageLoad(e);
            }}
            onError={() => setImgError(true)}
            className="w-full h-full pointer-events-none select-none"
            style={{
              objectFit: 'cover',
              objectPosition: `${offset.x}% ${offset.y}%`,
              transform: element.imageScale && element.imageScale > 1 ? `scale(${element.imageScale})` : undefined,
              transformOrigin: `${offset.x}% ${offset.y}%`,
              opacity: fullLoaded || !element.thumbnail ? 1 : 0,
            }}
          />
        </div>
      ) : (
        <div
          className="w-full h-full border-2 border-dashed border-muted-foreground/50 flex items-center justify-center cursor-pointer"
          style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
        >
          <span className="text-xs text-muted-foreground/70 pointer-events-none">Photo</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(SlotRenderer);